/**
 * AdminGlobalDocumentTypes — panel ITE „Globalne typy dokumentów"
 * (plan `glimmering-bouncing-hamster.md` §„Implementacja — frontend" F3).
 *
 * Centralny katalog typów dokumentów zarządzany przez administratorów ITE.
 * Opublikowany typ jest natychmiast dostępny u wszystkich tenantów (odczyt
 * na żywo z cache TTL 60 s po stronie backendu — poza zakresem tej strony).
 *
 * Segmentacja po 7 klasach dokumentów (jak `DocumentTypesTab` u tenanta).
 * Cykl życia: szkic → opublikowany → wycofany (bez hard-delete po publikacji;
 * szkic można skasować miękko). Kod jest niezmienialny po publikacji — backend
 * odrzuca zmianę 409 CODE_IMMUTABLE, modal blokuje edycję pola z podpowiedzią.
 *
 * Mutacje gatowane `hasPermission('global_document_types.manage')` — bez tego
 * uprawnienia user widzi tylko listę (GET wymaga `.view`).
 *
 * Endpoint: /api/admin/global-document-types.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AdminGlobalDocumentType,
  DocumentClass,
  GlobalDocumentTypeStatus,
} from '@saas/shared';
import { normalizeNumberingBaseSlug } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type { TFunction } from 'i18next';
import { Pencil, Plus, RotateCcw, Send, Trash2, Undo2 } from 'lucide-react';

import { InlineEditableCell } from '@/components/additional/InlineEditableCell';
import { Badge, type BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  type DefaultProcessTemplateOption,
  emptyGlobalDocumentTypeFormValues,
  type FormViewOption,
  GlobalDocumentTypeFormModal,
  type GlobalDocumentTypeFormValues,
} from '@/components/documents/GlobalDocumentTypeFormModal';
import { NumberingCell, type NumberingCellValue } from '@/components/documents/NumberingCell';
import { buildGlobalNumberingBaseFieldOptions } from '@/components/documents/useNumberingBaseFieldOptions';
import { Layout } from '@/components/Layout';
import { Skeleton } from '@/components/Skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { type ToastContextValue, useToast } from '@/contexts/ToastContext';
import { listAdminProcessTemplates } from '@/lib/admin-process-templates/api';
import { api } from '@/lib/api';
import { getKartotekaSlugForClass } from '@/lib/documents/class-kartoteka-slug';
import {
  buildCreatePayload,
  buildUpdatePayload,
  handleHrAbsenceSourceErrorCode,
  toFormValues,
} from '@/lib/documents/global-document-type-form';
import {
  useKartotekaDefinition,
  useKartotekaSystemViews,
} from '@/lib/kartoteki/kartoteka-definition';

/** ADR-0070 — cel procesu obszaru Dokumenty (stały, wspólny dla wszystkich 7 klas). */
const DOCUMENTS_PROCESS_TARGET_MODULE_CODE = 'crm_core.documents';

const ALL_CLASSES: DocumentClass[] = [
  'purchase_invoice',
  'sale_invoice',
  'contract',
  'letter',
  'commercial',
  'internal',
  'specjalne',
];

const MANAGE_PERMISSION = 'global_document_types.manage';

const STATUS_COLOR: Record<GlobalDocumentTypeStatus, BadgeColor> = {
  draft: 'gray',
  published: 'green',
  withdrawn: 'amber',
};

interface ListResponse {
  types: AdminGlobalDocumentType[];
}

type ApiErrorCode =
  | 'CODE_IMMUTABLE'
  | 'INVALID_STATUS'
  | 'CANNOT_DELETE_PUBLISHED'
  | 'CODE_TAKEN'
  | 'NAME_TAKEN'
  | 'VALIDATION_FAILED'
  | 'FORM_VIEW_NOT_FOUND'
  | 'FORM_VIEW_NOT_FORM'
  | 'FORM_VIEW_CLASS_MISMATCH'
  | 'DEFAULT_PROCESS_TEMPLATE_NOT_FOUND'
  | 'DEFAULT_PROCESS_TEMPLATE_NOT_PUBLISHED'
  | 'DEFAULT_PROCESS_TEMPLATE_MODULE_MISMATCH'
  | 'HR_ABSENCE_SOURCE_ALREADY_SET'
  | 'HR_ABSENCE_SOURCE_CLASS_NOT_ALLOWED';

function extractErrorCode(err: unknown): ApiErrorCode | null {
  if (!isAxiosError(err)) return null;
  return (err.response?.data?.error as ApiErrorCode | undefined) ?? null;
}

/**
 * Etap 3 (T3.5) — mapuje kody walidatora centralnego widoku formularza
 * (`validate-global-form-view-slug.ts`) na toast ostrzegawczy. Współdzielone przez
 * `onError` mutacji create/update (lustro `handleFormLayoutErrorCode` z tenantowego
 * `DocumentTypesTab.tsx`). Zwraca `true` gdy kod został obsłużony (caller przerywa
 * przed generycznym fallbackiem), `false` gdy kod nie jest jednym z tych trzech.
 */
function handleFormViewErrorCode(
  code: string | null,
  toast: ToastContextValue,
  t: TFunction,
): boolean {
  if (code === 'FORM_VIEW_NOT_FOUND') {
    toast.show({ variant: 'warning', title: t('adminDocumentTypes.toast.formViewNotFound') });
    return true;
  }
  if (code === 'FORM_VIEW_NOT_FORM') {
    toast.show({ variant: 'warning', title: t('adminDocumentTypes.toast.formViewNotForm') });
    return true;
  }
  if (code === 'FORM_VIEW_CLASS_MISMATCH') {
    toast.show({
      variant: 'warning',
      title: t('adminDocumentTypes.toast.formViewClassMismatch'),
    });
    return true;
  }
  return false;
}

/**
 * ADR-0070 — mapuje kody walidatora centralnego procesu domyślnego
 * (`validate-default-process-template.ts`) na toast ostrzegawczy. Lustro
 * `handleFormViewErrorCode` powyżej — te same wywołania w onError create/update.
 */
function handleDefaultProcessTemplateErrorCode(
  code: string | null,
  toast: ToastContextValue,
  t: TFunction,
): boolean {
  if (code === 'DEFAULT_PROCESS_TEMPLATE_NOT_FOUND') {
    toast.show({
      variant: 'warning',
      title: t('adminDocumentTypes.toast.defaultProcessTemplateNotFound'),
    });
    return true;
  }
  if (code === 'DEFAULT_PROCESS_TEMPLATE_NOT_PUBLISHED') {
    toast.show({
      variant: 'warning',
      title: t('adminDocumentTypes.toast.defaultProcessTemplateNotPublished'),
    });
    return true;
  }
  if (code === 'DEFAULT_PROCESS_TEMPLATE_MODULE_MISMATCH') {
    toast.show({
      variant: 'warning',
      title: t('adminDocumentTypes.toast.defaultProcessTemplateModuleMismatch'),
    });
    return true;
  }
  return false;
}

export function AdminGlobalDocumentTypes() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const canManage = hasPermission(MANAGE_PERMISSION);

  const [selectedClass, setSelectedClass] = useState<DocumentClass>('purchase_invoice');
  // Slug kartoteki systemowej tej klasy — adres katalogu centralnego (ADM) pól
  // dodatkowych ORAZ biblioteki widoków formularza (viewsQ niżej). Hoisted przed
  // wszystkie query, które go potrzebują.
  const kartotekaSlug = getKartotekaSlugForClass(selectedClass);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminGlobalDocumentType | null>(null);
  const [formServerErrors, setFormServerErrors] = useState<Record<string, string>>({});
  const [pendingPublish, setPendingPublish] = useState<AdminGlobalDocumentType | null>(null);
  const [pendingWithdraw, setPendingWithdraw] = useState<AdminGlobalDocumentType | null>(null);
  const [pendingRestore, setPendingRestore] = useState<AdminGlobalDocumentType | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminGlobalDocumentType | null>(null);
  const [filterCode, setFilterCode] = useState('');
  const [filterName, setFilterName] = useState('');

  // MEMOIZOWANE (code-review debt, obszar numeracji, F20) — inline `editing ? toFormValues(editing)
  // : emptyGlobalDocumentTypeFormValues()` tworzyłoby NOWY obiekt przy KAŻDYM renderze tej
  // strony. `GlobalDocumentTypeFormModal` resetuje stan formularza w efekcie zależnym od
  // `initialValues` (real deps, patrz ten komponent) — niestabilna referencja odpalałaby reset
  // przy KAŻDYM nieistotnym re-renderze (np. odświeżeniu `numberingBaseFieldOptions`),
  // wycierając niezapisaną edycję w trakcie pisania. `editing` jest stabilną referencją stanu
  // (zmienia się TYLKO przy otwarciu/zamknięciu modala na konkretnym wierszu — `setEditing`
  // wołane wyłącznie tam), więc memoizacja keyowana nim naprawia problem.
  const formInitialValues = useMemo(
    () => (editing ? toFormValues(editing) : emptyGlobalDocumentTypeFormValues()),
    [editing],
  );

  // Katalog CENTRALNY (ADM) pól dodatkowych tej klasy, do pickera „Pole daty numeracji" —
  // panel ITE nie ma pól ext tenanta, ale MA własny katalog centralny
  // (`system_kartoteka_fields`). Dwie definicje, bo reguła dozwolonego zbioru zależy od
  // kontekstu (mirror walidacji serwerowej POST/PUT, `resolveCentralExtDateSlugs`):
  //  - widok KLASY (bez typeId) — tylko pola wspólne; zasila wiersze tabeli (NumberingCell,
  //    współdzielone między wszystkimi typami klasy) ORAZ modal w trybie tworzenia.
  //  - widok TYPU (`editing.id`) — pola wspólne ∪ prywatne TEGO typu; zasila modal w trybie
  //    edycji WYŁĄCZNIE (enabled tylko gdy `editing !== null`).
  const classDefinitionQ = useKartotekaDefinition(kartotekaSlug);
  const editDefinitionQ = useKartotekaDefinition(editing ? kartotekaSlug : undefined, editing?.id);
  const numberingBaseFieldOptions = useMemo(
    () => buildGlobalNumberingBaseFieldOptions(selectedClass, classDefinitionQ.data?.fields, t),
    [selectedClass, classDefinitionQ.data, t],
  );
  const editingNumberingBaseFieldOptions = useMemo(
    () => buildGlobalNumberingBaseFieldOptions(selectedClass, editDefinitionQ.data?.fields, t),
    [selectedClass, editDefinitionQ.data, t],
  );

  const queryKey = useMemo(
    () => ['admin', 'global-document-types', selectedClass] as const,
    [selectedClass],
  );

  const listQ = useQuery<AdminGlobalDocumentType[]>({
    queryKey,
    queryFn: async () => {
      const res = await api.get<ListResponse>(
        `/admin/global-document-types?class=${selectedClass}`,
      );
      return res.data.types;
    },
    retry: false,
  });

  // Etap 3 (T3.5) — widoki FORM biblioteki kartoteki tej klasy, do pickera „Widok
  // formularza (centralnie)" w GlobalDocumentTypeFormModal. Keyowane slugiem kartoteki
  // (przełączenie zakładki = inna kartoteka); enabled TYLKO gdy modal jest otwarty
  // (lustro `layoutsModalOpen` z tenantowego DocumentTypesTab).
  const formViewsModalOpen = createOpen || editing !== null;
  const viewsQ = useKartotekaSystemViews(formViewsModalOpen ? kartotekaSlug : undefined);
  const formViewOptions: FormViewOption[] = (viewsQ.data ?? [])
    .filter((v) => v.viewType === 'form')
    .map((v) => ({ slug: v.slug, name: v.name }));
  const formViewsUnavailable = viewsQ.isError;

  // ADR-0070 — opublikowane szablony procesu celu `crm_core.documents`, do pickera
  // „Domyślny proces" ORAZ do wyświetlenia nazwy przypisanego szablonu w wierszu
  // tabeli. W przeciwieństwie do `formViews` cel jest stały dla wszystkich 7 klas
  // (nie keyowane `selectedClass`) i lista jest potrzebna zawsze (kolumna tabeli),
  // więc zapytanie NIE jest gatowane stanem otwarcia modala.
  const templatesQ = useQuery({
    queryKey: ['admin', 'process-templates', DOCUMENTS_PROCESS_TARGET_MODULE_CODE] as const,
    queryFn: () =>
      listAdminProcessTemplates({
        isPublished: true,
        targetModuleCode: DOCUMENTS_PROCESS_TARGET_MODULE_CODE,
      }),
    retry: false,
  });
  const templateOptions: DefaultProcessTemplateOption[] = (templatesQ.data?.data ?? []).map(
    (tpl) => ({ id: tpl.id, name: tpl.name }),
  );
  const templatesUnavailable = templatesQ.isError;

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['admin', 'global-document-types', selectedClass] });
  }

  const createMutation = useMutation({
    mutationFn: async (values: GlobalDocumentTypeFormValues) => {
      const res = await api.post<{ type: AdminGlobalDocumentType }>(
        '/admin/global-document-types',
        buildCreatePayload(selectedClass, values),
      );
      return res.data.type;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('adminDocumentTypes.toast.created') });
      setCreateOpen(false);
      setFormServerErrors({});
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      if (handleFormViewErrorCode(code, toast, t)) return;
      if (handleDefaultProcessTemplateErrorCode(code, toast, t)) return;
      if (code === 'CODE_TAKEN') {
        setFormServerErrors({ code: t('adminDocumentTypes.toast.codeTaken') });
        return;
      }
      if (code === 'NAME_TAKEN') {
        setFormServerErrors({ name: t('adminDocumentTypes.toast.nameTaken') });
        return;
      }
      if (handleHrAbsenceSourceErrorCode(code, toast, t)) return;
      toast.show({ variant: 'error', title: t('adminDocumentTypes.toast.createError') });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      values,
      original,
    }: {
      id: string;
      values: GlobalDocumentTypeFormValues;
      original: AdminGlobalDocumentType;
    }) => {
      const res = await api.put<{ type: AdminGlobalDocumentType }>(
        `/admin/global-document-types/${id}`,
        buildUpdatePayload(values, original),
      );
      return res.data.type;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('adminDocumentTypes.toast.updated') });
      setEditing(null);
      setFormServerErrors({});
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      if (handleFormViewErrorCode(code, toast, t)) return;
      if (handleDefaultProcessTemplateErrorCode(code, toast, t)) return;
      if (code === 'CODE_TAKEN') {
        setFormServerErrors({ code: t('adminDocumentTypes.toast.codeTaken') });
        return;
      }
      if (code === 'NAME_TAKEN') {
        setFormServerErrors({ name: t('adminDocumentTypes.toast.nameTaken') });
        return;
      }
      if (code === 'CODE_IMMUTABLE') {
        toast.show({ variant: 'warning', title: t('adminDocumentTypes.toast.codeImmutable') });
        return;
      }
      if (handleHrAbsenceSourceErrorCode(code, toast, t)) return;
      toast.show({ variant: 'error', title: t('adminDocumentTypes.toast.updateError') });
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ type: AdminGlobalDocumentType }>(
        `/admin/global-document-types/${id}/publish`,
      );
      return res.data.type;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('adminDocumentTypes.toast.published') });
      setPendingPublish(null);
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      if (code === 'INVALID_STATUS') {
        toast.show({ variant: 'warning', title: t('adminDocumentTypes.toast.invalidStatus') });
        setPendingPublish(null);
        return;
      }
      toast.show({ variant: 'error', title: t('adminDocumentTypes.toast.publishError') });
      setPendingPublish(null);
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ type: AdminGlobalDocumentType }>(
        `/admin/global-document-types/${id}/withdraw`,
      );
      return res.data.type;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('adminDocumentTypes.toast.withdrawn') });
      setPendingWithdraw(null);
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      if (code === 'INVALID_STATUS') {
        toast.show({ variant: 'warning', title: t('adminDocumentTypes.toast.invalidStatus') });
        setPendingWithdraw(null);
        return;
      }
      toast.show({ variant: 'error', title: t('adminDocumentTypes.toast.withdrawError') });
      setPendingWithdraw(null);
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ type: AdminGlobalDocumentType }>(
        `/admin/global-document-types/${id}/restore`,
      );
      return res.data.type;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('adminDocumentTypes.toast.restored') });
      setPendingRestore(null);
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      if (code === 'INVALID_STATUS') {
        toast.show({ variant: 'warning', title: t('adminDocumentTypes.toast.invalidStatus') });
        setPendingRestore(null);
        return;
      }
      toast.show({ variant: 'error', title: t('adminDocumentTypes.toast.restoreError') });
      setPendingRestore(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/global-document-types/${id}`);
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('adminDocumentTypes.toast.deleted') });
      setPendingDelete(null);
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      if (code === 'CANNOT_DELETE_PUBLISHED') {
        toast.show({
          variant: 'warning',
          title: t('adminDocumentTypes.toast.cannotDeletePublished'),
        });
        setPendingDelete(null);
        return;
      }
      toast.show({ variant: 'error', title: t('adminDocumentTypes.toast.deleteError') });
      setPendingDelete(null);
    },
  });

  // Inline-edit (Kod/Nazwa/Kolejność/Maska numeracji) — jedna wspólna mutacja PUT
  // (backend akceptuje częściowy payload). Błąd 409/422 mapowany na przetłumaczony
  // string i rzucany jako Error, żeby InlineEditableCell/NumberingCell mogły
  // zostawić komórkę otwartą z komunikatem (kontrakt onCommit: void | Promise<void>).
  const inlineUpdateMutation = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) => {
      const res = await api.put<{ type: AdminGlobalDocumentType }>(
        `/admin/global-document-types/${id}`,
        patch,
      );
      return res.data.type;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('adminDocumentTypes.toast.updated') });
    },
  });

  function mapInlineErrorMessage(code: ApiErrorCode | null): string {
    if (code === 'CODE_TAKEN') return t('adminDocumentTypes.toast.codeTaken');
    if (code === 'NAME_TAKEN') return t('adminDocumentTypes.toast.nameTaken');
    if (code === 'CODE_IMMUTABLE') return t('adminDocumentTypes.toast.codeImmutable');
    if (code === 'VALIDATION_FAILED') return t('adminDocumentTypes.toast.validationFailed');
    return t('adminDocumentTypes.toast.updateError');
  }

  async function commitField(id: string, patch: Record<string, unknown>): Promise<void> {
    try {
      await inlineUpdateMutation.mutateAsync({ id, patch });
    } catch (err) {
      throw new Error(mapInlineErrorMessage(extractErrorCode(err)), { cause: err });
    }
  }

  // ADR-0070 — komórka tabeli „Domyślny proces": badge z nazwą szablonu, „—" gdy
  // brak wskazania, neutralny tekst gdy wskazany szablon zniknął z bieżącej listy
  // (lustro syntetycznej opcji „niedostępny" w pickerze modala).
  function renderAssignedTemplateCell(templateId: string | null) {
    if (!templateId) {
      return <span className="text-text-muted">—</span>;
    }
    const tpl = templateOptions.find((t) => t.id === templateId);
    if (!tpl) {
      return (
        <span className="text-xs text-text-muted">
          {t('adminDocumentTypes.form.defaultProcessTemplateMissing')}
        </span>
      );
    }
    return <Badge color="blue">{tpl.name}</Badge>;
  }

  const types = useMemo(() => listQ.data ?? [], [listQ.data]);

  const filteredTypes = useMemo(() => {
    const codeQuery = filterCode.trim().toLowerCase();
    const nameQuery = filterName.trim().toLowerCase();
    if (!codeQuery && !nameQuery) return types;
    return types.filter(
      (dt) =>
        (!codeQuery || dt.code.toLowerCase().includes(codeQuery)) &&
        (!nameQuery || dt.name.toLowerCase().includes(nameQuery)),
    );
  }, [types, filterCode, filterName]);

  return (
    <Layout>
      <section className="px-6 py-8">
        <header className="mb-6 flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{t('adminDocumentTypes.title')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('adminDocumentTypes.subtitle')}
            </p>
          </div>
          {canManage && (
            <Button
              variant="primary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setFormServerErrors({});
                setCreateOpen(true);
              }}
              data-testid="global-document-type-new-btn"
            >
              {t('adminDocumentTypes.newType')}
            </Button>
          )}
        </header>

        {/* 7-klasowy segmented control */}
        <div
          role="tablist"
          aria-label={t('adminDocumentTypes.title')}
          className="mb-4 flex gap-1 rounded-lg bg-bg-secondary p-1"
        >
          {ALL_CLASSES.map((cls) => (
            <button
              key={cls}
              type="button"
              role="tab"
              aria-selected={selectedClass === cls}
              onClick={() => setSelectedClass(cls)}
              data-testid={`global-document-types-class-${cls}`}
              className={
                'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ' +
                (selectedClass === cls
                  ? 'bg-bg-primary text-text-primary shadow-sm'
                  : 'text-text-secondary hover:text-text-primary')
              }
            >
              {t(`adminDocumentTypes.classes.${cls}`)}
            </button>
          ))}
        </div>

        {listQ.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : listQ.isError ? (
          <div
            role="alert"
            className="rounded border border-red-300 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
          >
            {isAxiosError(listQ.error) && listQ.error.response?.status === 403
              ? t('adminDocumentTypes.noPermission')
              : t('adminDocumentTypes.loadError')}
          </div>
        ) : types.length === 0 ? (
          <Card padding="md">
            <p className="text-sm text-text-tertiary">{t('adminDocumentTypes.empty')}</p>
          </Card>
        ) : (
          <Card padding="none">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border-primary bg-bg-secondary text-xs uppercase tracking-wider text-text-muted">
                  <tr>
                    <th className="px-4 py-2 text-left">{t('adminDocumentTypes.columns.code')}</th>
                    <th className="px-4 py-2 text-left">{t('adminDocumentTypes.columns.name')}</th>
                    <th className="px-4 py-2 text-left">
                      {t('adminDocumentTypes.columns.status')}
                    </th>
                    <th className="px-4 py-2 text-left">
                      {t('adminDocumentTypes.columns.numberingFormat')}
                    </th>
                    <th className="px-4 py-2 text-left">
                      {t('adminDocumentTypes.columns.sortOrder')}
                    </th>
                    <th className="px-4 py-2 text-left">
                      {t('adminDocumentTypes.columns.defaultProcessTemplate')}
                    </th>
                    {canManage && (
                      <th className="px-4 py-2 text-right">
                        {t('adminDocumentTypes.columns.actions')}
                      </th>
                    )}
                  </tr>
                  <tr data-testid="global-document-types-filter-row">
                    <th className="px-4 pb-2">
                      <input
                        type="text"
                        value={filterCode}
                        onChange={(e) => setFilterCode(e.target.value)}
                        placeholder={t('adminDocumentTypes.filterCode')}
                        aria-label={t('adminDocumentTypes.filterCode')}
                        className="w-full rounded-lg border border-border-primary bg-bg-primary px-2.5 py-1.5 text-xs font-normal normal-case tracking-normal text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-500"
                        data-testid="global-document-types-filter-code"
                      />
                    </th>
                    <th className="px-4 pb-2">
                      <input
                        type="text"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        placeholder={t('adminDocumentTypes.filterName')}
                        aria-label={t('adminDocumentTypes.filterName')}
                        className="w-full rounded-lg border border-border-primary bg-bg-primary px-2.5 py-1.5 text-xs font-normal normal-case tracking-normal text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-500"
                        data-testid="global-document-types-filter-name"
                      />
                    </th>
                    <th className="px-4 pb-2" />
                    <th className="px-4 pb-2" />
                    <th className="px-4 pb-2" />
                    <th className="px-4 pb-2" />
                    {canManage && <th className="px-4 pb-2" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-primary">
                  {filteredTypes.length === 0 && (
                    <tr>
                      <td
                        colSpan={canManage ? 7 : 6}
                        className="px-4 py-6 text-center text-sm text-text-tertiary"
                      >
                        {t('adminDocumentTypes.noFilterResults')}
                      </td>
                    </tr>
                  )}
                  {filteredTypes.map((dt) => (
                    <tr key={dt.id} data-testid={`global-document-type-row-${dt.id}`}>
                      <td className="px-4 py-3">
                        {canManage && dt.status === 'draft' ? (
                          <InlineEditableCell<string>
                            value={dt.code}
                            onCommit={(newVal) => commitField(dt.id, { code: newVal })}
                            renderInput={(
                              draft,
                              onChange,
                              onKeyDown,
                              onBlur,
                              ref,
                              isCommitting,
                            ) => (
                              <input
                                ref={ref as React.RefObject<HTMLInputElement>}
                                value={draft}
                                onChange={(e) => onChange(e.target.value)}
                                onKeyDown={onKeyDown}
                                onBlur={onBlur}
                                disabled={isCommitting}
                                className="w-full rounded border border-accent-500 bg-bg-primary px-2 py-0.5 font-mono text-xs text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent-500"
                                data-testid={`global-document-type-code-input-${dt.id}`}
                              />
                            )}
                            renderDisplay={(v) => (
                              <code className="rounded bg-bg-secondary px-1.5 py-0.5 text-xs text-text-secondary">
                                {v}
                              </code>
                            )}
                            className="-mx-2 rounded px-2 py-1 hover:bg-bg-tertiary"
                            data-testid={`global-document-type-code-${dt.id}`}
                          />
                        ) : (
                          <code className="rounded bg-bg-secondary px-1.5 py-0.5 text-xs text-text-secondary">
                            {dt.code}
                          </code>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium text-text-primary">
                        <div className="flex items-center gap-2">
                          {canManage ? (
                            <InlineEditableCell<string>
                              value={dt.name}
                              onCommit={(newVal) => commitField(dt.id, { name: newVal })}
                              renderInput={(
                                draft,
                                onChange,
                                onKeyDown,
                                onBlur,
                                ref,
                                isCommitting,
                              ) => (
                                <input
                                  ref={ref as React.RefObject<HTMLInputElement>}
                                  value={draft}
                                  onChange={(e) => onChange(e.target.value)}
                                  onKeyDown={onKeyDown}
                                  onBlur={onBlur}
                                  disabled={isCommitting}
                                  className="w-full rounded border border-accent-500 bg-bg-primary px-2 py-0.5 text-sm font-medium text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
                                  data-testid={`global-document-type-name-input-${dt.id}`}
                                />
                              )}
                              className="-mx-2 rounded px-2 py-1 hover:bg-bg-tertiary"
                              data-testid={`global-document-type-name-${dt.id}`}
                            />
                          ) : (
                            dt.name
                          )}
                          {dt.isHrAbsenceSource && (
                            <Badge
                              color="blue"
                              data-testid={`global-document-type-hr-absence-badge-${dt.id}`}
                            >
                              {t('adminDocumentTypes.hrAbsenceSourceBadge')}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge color={STATUS_COLOR[dt.status]}>
                          {t(`adminDocumentTypes.status.${dt.status}`)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-text-secondary">
                        {canManage ? (
                          <NumberingCell
                            code={dt.code}
                            baseFieldOptions={numberingBaseFieldOptions}
                            value={{
                              numberingFormat: dt.numberingFormat ?? '',
                              numberingBaseField: normalizeNumberingBaseSlug(
                                dt.class,
                                dt.numberingBaseField,
                              ),
                              numberingReset: dt.numberingReset,
                            }}
                            onCommit={(next: NumberingCellValue) =>
                              commitField(dt.id, {
                                numberingFormat: next.numberingFormat.trim() || null,
                                numberingBaseField: next.numberingBaseField,
                                numberingReset: next.numberingReset,
                              })
                            }
                            data-testid={`global-document-type-numbering-${dt.id}`}
                          />
                        ) : (
                          (dt.numberingFormat ?? <span className="text-text-muted">—</span>)
                        )}
                      </td>
                      <td className="px-4 py-3 text-text-secondary">
                        {canManage ? (
                          <InlineEditableCell<number>
                            value={dt.sortOrder}
                            isEqual={(a, b) => Number(a) === Number(b)}
                            onCommit={(newVal) => commitField(dt.id, { sortOrder: newVal })}
                            renderInput={(
                              draft,
                              onChange,
                              onKeyDown,
                              onBlur,
                              ref,
                              isCommitting,
                            ) => (
                              <input
                                ref={ref as React.RefObject<HTMLInputElement>}
                                type="number"
                                min={0}
                                value={draft}
                                onChange={(e) => {
                                  const parsed = parseInt(e.target.value, 10);
                                  onChange(Number.isNaN(parsed) ? 0 : Math.max(0, parsed));
                                }}
                                onKeyDown={onKeyDown}
                                onBlur={onBlur}
                                disabled={isCommitting}
                                className="w-20 rounded border border-accent-500 bg-bg-primary px-2 py-0.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
                                data-testid={`global-document-type-sort-order-input-${dt.id}`}
                              />
                            )}
                            className="-mx-2 rounded px-2 py-1 hover:bg-bg-tertiary"
                            data-testid={`global-document-type-sort-order-${dt.id}`}
                          />
                        ) : (
                          dt.sortOrder
                        )}
                      </td>
                      <td
                        className="px-4 py-3"
                        data-testid={`global-document-type-default-process-template-${dt.id}`}
                      >
                        {renderAssignedTemplateCell(dt.defaultProcessTemplateId)}
                      </td>
                      {canManage && (
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => {
                                setFormServerErrors({});
                                setEditing(dt);
                              }}
                              aria-label={t('adminDocumentTypes.actions.edit')}
                              className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                              data-testid={`global-document-type-edit-${dt.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            {dt.status === 'draft' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setPendingPublish(dt)}
                                  aria-label={t('adminDocumentTypes.actions.publish')}
                                  className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-accent-600"
                                  data-testid={`global-document-type-publish-${dt.id}`}
                                >
                                  <Send className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setPendingDelete(dt)}
                                  aria-label={t('adminDocumentTypes.actions.delete')}
                                  className="rounded p-1.5 text-text-muted transition hover:bg-error-500/10 hover:text-error-500"
                                  data-testid={`global-document-type-delete-${dt.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </>
                            )}
                            {dt.status === 'published' && (
                              <button
                                type="button"
                                onClick={() => setPendingWithdraw(dt)}
                                aria-label={t('adminDocumentTypes.actions.withdraw')}
                                className="rounded p-1.5 text-text-muted transition hover:bg-error-500/10 hover:text-error-500"
                                data-testid={`global-document-type-withdraw-${dt.id}`}
                              >
                                <Undo2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {dt.status === 'withdrawn' && (
                              <button
                                type="button"
                                onClick={() => setPendingRestore(dt)}
                                aria-label={t('adminDocumentTypes.actions.restore')}
                                className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                                data-testid={`global-document-type-restore-${dt.id}`}
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      {/* Modal create / edit */}
      <GlobalDocumentTypeFormModal
        open={createOpen || editing !== null}
        mode={editing ? 'edit' : 'create'}
        documentClass={selectedClass}
        // Tryb tworzenia: pola centralne WSPÓLNE klasy (mirror walidacji POST). Tryb
        // edycji: pola wspólne ∪ prywatne TEGO typu (mirror walidacji PUT, `editing.id`).
        baseFieldOptions={editing ? editingNumberingBaseFieldOptions : numberingBaseFieldOptions}
        initialValues={formInitialValues}
        codeLocked={editing ? editing.status !== 'draft' : false}
        saving={createMutation.isPending || updateMutation.isPending}
        serverErrors={formServerErrors}
        formViews={formViewOptions}
        formViewsUnavailable={formViewsUnavailable}
        templates={templateOptions}
        templatesUnavailable={templatesUnavailable}
        onSubmit={(values) => {
          setFormServerErrors({});
          if (editing) {
            updateMutation.mutate({ id: editing.id, values, original: editing });
          } else {
            createMutation.mutate(values);
          }
        }}
        onClose={() => {
          setCreateOpen(false);
          setEditing(null);
          setFormServerErrors({});
        }}
      />

      {/* Potwierdzenie publikacji — nie-destrukcyjne, ale nieodwracalne dla kodu (variant primary) */}
      <ConfirmDialog
        open={pendingPublish !== null}
        title={t('adminDocumentTypes.confirmPublish.title')}
        message={
          pendingPublish
            ? t('adminDocumentTypes.confirmPublish.message', {
                name: pendingPublish.name,
                code: pendingPublish.code,
              })
            : ''
        }
        confirmLabel={t('adminDocumentTypes.confirmPublish.confirm')}
        variant="primary"
        onConfirm={() => {
          if (pendingPublish) publishMutation.mutate(pendingPublish.id);
        }}
        onClose={() => setPendingPublish(null)}
      />

      {/* Potwierdzenie wycofania — destrukcyjne dla widoczności u tenantów (variant danger) */}
      <ConfirmDialog
        open={pendingWithdraw !== null}
        title={t('adminDocumentTypes.confirmWithdraw.title')}
        message={
          pendingWithdraw
            ? t('adminDocumentTypes.confirmWithdraw.message', {
                name: pendingWithdraw.name,
                code: pendingWithdraw.code,
              })
            : ''
        }
        confirmLabel={t('adminDocumentTypes.confirmWithdraw.confirm')}
        variant="danger"
        onConfirm={() => {
          if (pendingWithdraw) withdrawMutation.mutate(pendingWithdraw.id);
        }}
        onClose={() => setPendingWithdraw(null)}
      />

      {/* Potwierdzenie przywrócenia — nie-destrukcyjne (variant primary) */}
      <ConfirmDialog
        open={pendingRestore !== null}
        title={t('adminDocumentTypes.confirmRestore.title')}
        message={
          pendingRestore
            ? t('adminDocumentTypes.confirmRestore.message', {
                name: pendingRestore.name,
                code: pendingRestore.code,
              })
            : ''
        }
        confirmLabel={t('adminDocumentTypes.confirmRestore.confirm')}
        variant="primary"
        onConfirm={() => {
          if (pendingRestore) restoreMutation.mutate(pendingRestore.id);
        }}
        onClose={() => setPendingRestore(null)}
      />

      {/* Potwierdzenie usunięcia szkicu — destrukcyjne (variant danger) */}
      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('adminDocumentTypes.confirmDelete.title')}
        message={
          pendingDelete
            ? t('adminDocumentTypes.confirmDelete.message', {
                name: pendingDelete.name,
                code: pendingDelete.code,
              })
            : ''
        }
        confirmLabel={t('adminDocumentTypes.confirmDelete.confirm')}
        variant="danger"
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </Layout>
  );
}
